import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Typography,
  Box,
  InputLabel,
  Skeleton,
  Grid,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import { useUserContext } from "../../../context/UserContext";
import {
  getMappedBusinessEntityIDs,
  maxDate,
  minDate,
} from "../../../utils/utils";
import dayjs from "dayjs";
import { useApprovalContext } from "../../../context/ApprovalContext";
import DatePickerMonthEnds from "../../common/DatePicker/DatePickerMonthEnds";

// Filter used for both NII Sensitivity and Advanced NII Sensitivity
const NIISensitivityFilters = ({
  toCompare = false,
  setIsAdvancedNII,
  isAdvancedNII,
  setIsWritableSfc,
  handleSubmit,
}) => {
  const [isTouched, setIsTouched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    allowedMonthEnds,
    selectedModelDatabase,
    setModelDatabases,
    setSelectedModelDatabase,
    modelDatabases,
    selectedMonthEnd,
    setSelectedMonthEnd,
    setLocalCurrency,
    selectedFigureDivisor,
    selectedDecimalsIn,
    setHrGapResultData,
    hrGapResultDataLoading,
  } = useHedgeRatioContext();

  const { setApprovalFlow } = useApprovalContext();

  const { regions } = selectedUserDetail || {};

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    setModelDatabases(entityObject?.dateModels || []);
    const { localCurrency: currency } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
    setIsWritableSfc,
  ]);

  useEffect(() => {
    const modelObject = modelDatabases?.find((model) => {
      if (model?.monthEnd) {
        const datePart = model.monthEnd?.slice(0, 10);
        return datePart === selectedMonthEnd?.format("YYYY-MM-DD");
      }
      return false;
    });
    setSelectedModelDatabase(modelObject?.modelName || null);
  }, [modelDatabases, selectedMonthEnd, setSelectedModelDatabase]);

  const handleOpenChange = (open) => {
    if (!open) {
      setIsTouched(true);
    }
    setIsOpen(open);
  };

  const onSubmitClick = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      const selectedModelObject = modelDatabases?.find(
        (modelDb) => modelDb.modelName === selectedModelDatabase,
      );

      const advancedNII = selectedModelObject?.advancedNII === true;

      setIsAdvancedNII(advancedNII);

      handleSubmit({
        selectedBusinessEntityID,
        selectedModelDatabase,
        selectedMonthEnd: selectedModelObject?.monthEnd
          ? dayjs(selectedModelObject.monthEnd.replace("Z", ""))
          : null,
        advancedNII,
        selectedFigureDivisor,
        selectedDecimalsIn,
      });
    } else {
      setHrGapResultData([]);
      setApprovalFlow([]);
      alert(
        "The selected Entity / Month End does not have any Model Database associated (or) it does not have Treasury KRI Global View.",
      );
    }
  };
  return (
    <>
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below data shows the {isAdvancedNII ? "Advanced NII" : "NII"}{" "}
        Sensitivity Report (Global View) based on the selected filters.
      </Typography>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"report-date"}>Month End & Year</InputLabel>
          {selectedUserDetailLoading || hrFiltersDataLoading ? (
            <Box display="flex" alignItems="center" gap={2}>
              <Skeleton variant="rectangular" width={185} height={40} />
            </Box>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={2} mb={0}>
                <DatePickerMonthEnds
                  minDate={minDate}
                  maxDate={maxDate}
                  allowedMonthEnds={allowedMonthEnds}
                  value={selectedMonthEnd}
                  onChange={(newValue) => {
                    setSelectedMonthEnd(newValue?.endOf("month"));
                  }}
                  onOpenChange={handleOpenChange}
                  isOpen={isOpen}
                />
              </Box>
              <Box>
                {selectedMonthEnd === null && isTouched && (
                  <span
                    style={{
                      color: "red",
                      fontSize: "12px",
                      fontStyle: "italic",
                    }}
                  >
                    Month End & Year is required
                  </span>
                )}
              </Box>
            </>
          )}
        </Grid>

        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onSubmitClick}
          sx={{
            fontSize: "0.8rem",
            boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
            height: "38px",
            mt: 3,
          }}
          disabled={
            selectedUserDetailLoading ||
            hrFiltersDataLoading ||
            hrGapResultDataLoading ||
            !selectedBusinessEntityID ||
            !selectedMonthEnd
          }
        >
          Submit
        </Button>
      </Grid>
    </>
  );
};

export default NIISensitivityFilters;
